// Season-progress modal: a 30-row ladder of milestones and the cosmetic
// rewards the player earns at each one. Mirrors the lifetimeRuns unlock
// thresholds defined in `local/unlocks.ts` so the in-game progression and
// the visible ladder stay in sync automatically.

import { useMemo } from "react";
import { UNLOCKS, type UnlockItem } from "../local/unlocks";

interface Props {
  /** Player's current lifetime-runs count, used to mark progress. */
  currentRuns: number;
  onClose: () => void;
}

const TOTAL_LEVELS = 30;

export function SeasonProgress({ currentRuns, onClose }: Props) {
  // Build a level → reward map. Each "level" is just a lifetimeRuns
  // milestone. We bucket every UNLOCKS item by its `unlockAt`, then walk
  // 1..TOTAL_LEVELS pulling whichever reward (if any) lands on that exact
  // milestone. Levels with no item get a coin/star reward placeholder so
  // every row feels rewarding.
  const rewardsByLevel = useMemo(() => {
    const map = new Map<number, UnlockItem[]>();
    for (const u of UNLOCKS) {
      if (u.unlockAt < 1 || u.unlockAt > TOTAL_LEVELS) continue;
      const arr = map.get(u.unlockAt) ?? [];
      arr.push(u);
      map.set(u.unlockAt, arr);
    }
    return map;
  }, []);

  return (
    <div className="season-overlay" role="dialog" aria-modal="true" aria-label="Season progress" onClick={onClose}>
      <div className="season-card popup-allow-overflow" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
        <button className="popup-close-x" onClick={onClose} aria-label="Close">×</button>
        <div className="season-card-scroll" style={{ maxHeight: "78vh", overflowY: "auto", overflowX: "hidden", paddingRight: 4 }}>
          <h2>Season 1 Progress</h2>
          <p className="t-muted" style={{ marginTop: 0 }}>
            Finish runs to climb levels and unlock new gear. You're currently on
            run <strong>{currentRuns}</strong>.
          </p>
          <div className="season-rows">
          {Array.from({ length: TOTAL_LEVELS }, (_, i) => i + 1).map((level) => {
            const rewards = rewardsByLevel.get(level) ?? [];
            const isCurrent = currentRuns + 1 === level || (currentRuns === level && currentRuns < TOTAL_LEVELS);
            const isLocked = currentRuns < level;
            return (
              <div
                key={level}
                className={`season-row ${isCurrent ? "is-current" : ""} ${isLocked ? "is-locked" : ""}`}
              >
                <div className="season-row-badge">{isLocked ? "🔒" : level}</div>
                <div className="season-row-meta">
                  <strong>Level {level}</strong>
                  <span>
                    {rewards.length === 0
                      ? "Coins + XP"
                      : rewards.map((r) => r.name).join(" · ")}
                  </span>
                </div>
                {rewards.length > 0 && (
                  <div className="season-row-reward">
                    {rewards.slice(0, 2).map((r) => (
                      <SeasonRewardThumb key={r.id} item={r} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
}

function SeasonRewardThumb({ item }: { item: UnlockItem }) {
  if (item.kind === "shirt-color") {
    return (
      <span
        className="season-thumb"
        style={{ background: item.apply.shirtColor ?? "#888" }}
        title={item.name}
      />
    );
  }
  if (item.kind === "pattern") {
    const bg = patternBg(item.apply.patternId ?? "solid");
    return <span className="season-thumb" style={{ background: bg }} title={item.name} />;
  }
  // Hat — emoji on a colour chip is a clean small thumbnail.
  const emoji = hatEmoji(item.apply.hatId ?? "none");
  return (
    <span
      className="season-thumb"
      style={{
        background: "linear-gradient(180deg, #ffd166, #f97316)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      title={item.name}
    >
      <span style={{ fontSize: 18 }}>{emoji}</span>
    </span>
  );
}

function patternBg(id: string): string {
  if (id === "stripes") return "repeating-linear-gradient(45deg, #ff2ca4 0 6px, #fff 6px 12px)";
  if (id === "dots") return "radial-gradient(#fff 22%, transparent 26%) #ff2ca4";
  if (id === "star") return "radial-gradient(#ffd166 18%, transparent 22%) #ff2ca4";
  if (id === "checker") return "repeating-conic-gradient(#ff2ca4 0 25%, #fff 0 50%) 50% / 16px 16px";
  if (id === "zigzag") return "repeating-linear-gradient(135deg, #ff2ca4 0 6px, #ffe066 6px 12px)";
  if (id === "hearts") return "radial-gradient(#fff 22%, transparent 26%) #ff2ca4";
  if (id === "lightning") return "linear-gradient(45deg, #ffd166, #ff2ca4)";
  return "#ff2ca4";
}

function hatEmoji(id: string): string {
  if (id === "cap-blue" || id === "cap-red" || id === "cap-green" || id === "cap-purple") return "🧢";
  if (id === "beanie" || id === "beanie-blue" || id === "top-hat") return "🎩";
  if (id === "crown") return "👑";
  if (id === "party-hat") return "🥳";
  if (id === "halo") return "😇";
  if (id === "wizard") return "🧙";
  if (id === "horns") return "😈";
  if (id === "headband" || id === "bow") return "🎀";
  return "🚫";
}
